import { useCallback, useState } from 'react';
import { Size, mapsLinkPin, normalizePostalCode, suggestionRowsFor, t, validatePostalCode, type MapsLinkAnswer, type MapsLinkPin, type SearchFn, type SuggestionSource } from '@bms2/shared';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { Button, Dropdown, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms';
import { PAGES_PATH, PICKER_CAP, post, apiFetch } from './api';
import { useCompanySearch } from '../settings/api';
import styles from './RecordPage.module.css';
import { useAddNew } from './AddNewHost';
import { MapsLinkPaste, readMapsLinkAnswer } from './MapsLinkPaste';
import { SiteTypeField } from './SiteTypeField';

/**
 * The + Add Site combo modal (piece 13, decisions 0138, 0167 and 0232). 🔨 Two
 * steps rather than a modal inside a modal, because the Quick-Create wrapper's
 * contract has no rule for a stacked one. 🔨 Step two must save with every field
 * blank: a single-occupant building has no suite, and a Site with no active role
 * is a real state. There is no refusal here for our own Company.
 */
export interface SiteModalProps {
  open: boolean;
  /** Locked from the opening context: the company from a Company page, the building from a Location page. */
  companyId?: string | null;
  companyName?: string | null;
  locationId?: string | null;
  addressLine?: string | null;
  apiBaseUrl?: string;
  onCancel: () => void;
  /** The site's own label comes back too — its name, else its suite, else the building — so a picker can show what it picked (T311). */
  onCreated: (result: { siteId: string; locationId: string; warning: string | null; label?: string }) => void;
  openerId?: string | null;
  /**
   * A pasted Maps link's answer the opener already read: an event's Place field
   * found no place at its address (decision 1600). Step one opens filled from it,
   * and the save writes its point as the new building's pin. 🔨 Read when the
   * modal MOUNTS, so a host keys the modal to the answer.
   */
  fromLink?: MapsLinkAnswer | null;
  /** 🔒 Decision 5382: an Accepted address suggestion opens + Add Site with the suggested address in its address line; nothing is written until Save. Read when the modal mounts. */
  addressHint?: string | null;
  /** 🔒 Decision 6180: the suggested address's parts, its suite among them, laid into their own fields in place of the one line. */
  addressDetail?: Readonly<Record<string, string>> | null;
}

/** The first street line of a link's answer, or nothing. */
const linkLine = (answer: MapsLinkAnswer | null | undefined) => answer?.address?.addressLines[0] ?? '';

export function SiteModal(props: SiteModalProps) {
  const { open, companyId = null, companyName, locationId = null, addressLine, apiBaseUrl = '', onCancel, onCreated, openerId = null, fromLink = null, addressHint = null, addressDetail = null } = props;
  const addNew = useAddNew(apiBaseUrl);
  const [openStep, setStep] = useState<1 | 2>(locationId ? 2 : 1);
  // 🔨 What the opener laid in is not typed (decision 3061), so the building's fields are read against what the window mounted with.
  const [laidIn] = useState(() => (addressDetail?.street && !fromLink
    ? { addressLines: addressDetail.street, locality: addressDetail.locality ?? '', administrativeArea: addressDetail.region ?? '', postalCode: addressDetail.postalCode ?? '' }
    : { addressLines: linkLine(fromLink) || (addressHint ?? '').trim(), locality: fromLink?.address?.locality ?? '', administrativeArea: fromLink?.address?.administrativeArea ?? '', postalCode: fromLink?.address?.postalCode ?? '' }));
  const [addressLines, setAddressLines] = useState(laidIn.addressLines);
  const [locality, setLocality] = useState(laidIn.locality);
  const [administrativeArea, setAdministrativeArea] = useState(laidIn.administrativeArea);
  const [postalCode, setPostalCode] = useState(laidIn.postalCode);
  /** The point a pasted Maps link gave step one, written as the new building's pin (decision 1600). */
  const [linkPin, setLinkPin] = useState<MapsLinkPin | null>(() => mapsLinkPin(fromLink));
  const [suite, setSuite] = useState(addressDetail?.suite ?? '');
  const [name, setName] = useState('');
  /** 🔒 Decision 10100: what kind of place the site is, from the active types; never required. */
  const [siteTypeId, setSiteTypeId] = useState('');
  const [note, setNote] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // 🔨 Opened from the Company page, the Location page or a picker, the modal is
  // locked to one half. Opened from the Sites list there is no half yet, so it
  // collects the company itself; every company is selectable, ours included,
  // because a Site at our own Company is a Site like any other (decision 0232).
  const [chosen, setChosen] = useState<{ id: string; label: string } | null>(null);
  const forCompanyId = companyId ?? chosen?.id ?? null;
  // 🔨 Step one looks for the building before it types one: a picked row is the
  // building, and the address fields fold away. 💰 The picker was missing, so
  // every site typed at a known address made a second copy of the building
  // (found while building, 2026-09-09).
  const [picked, setPicked] = useState<{ id: string; label: string } | null>(null);
  const buildingId = locationId ?? picked?.id ?? null;

  const searchLocations = useCallback<SearchFn>(
    async (query, signal) => {
      const response = await apiFetch(`${apiBaseUrl}${PAGES_PATH}/list/LocationRecord?${PICKER_CAP}&q=${encodeURIComponent(query)}`, { signal });
      if (!response.ok) return [];
      const body = (await response.json()) as { rows: SuggestionSource[] };
      return suggestionRowsFor('LocationRecord', body.rows);
    },
    [apiBaseUrl],
  );
  const searchCompanies = useCompanySearch(apiBaseUrl);

  // A closed modal holds no step and no banner: both are derived from `open`,
  // the same shape the View as picker uses for its selection. Locked to a
  // building there is no step one to return to, so it opens on step two.
  const step: 1 | 2 = open ? openStep : locationId ? 2 : 1;
  const shownWarning = open ? warning : null;
  const shownError = open ? error : null;
  /** What the leave guard reads (decision 3061): any answer a person gave since the window opened. */
  const typed = picked !== null || chosen !== null || suite !== '' || name !== '' || siteTypeId !== '' || note !== ''
    || addressLines !== laidIn.addressLines || locality !== laidIn.locality || administrativeArea !== laidIn.administrativeArea || postalCode !== laidIn.postalCode;

  /**
   * The four-case collision rule, asked before the save so a warning is shown
   * rather than discovered on it. A refusal comes back from the write itself.
   */
  const checkCollision = useCallback(async () => {
    if (!buildingId || !forCompanyId) return;
    const answer = await post<{ verdict: string; reason: string | null; nearMatch: boolean }>(
      `${PAGES_PATH}/site-modal/collision`,
      { locationId: buildingId, companyId: forCompanyId, suite },
      apiBaseUrl,
    );
    if (!answer.ok) return;
    setWarning(answer.data.verdict === 'warn' ? answer.data.reason : answer.data.nearMatch ? t('siteModal.nearMatch') : null);
  }, [apiBaseUrl, forCompanyId, buildingId, suite]);

  const save = useCallback(async () => {
    setError(null);
    if (!forCompanyId) {
      setError(t('siteModal.needsCompany'));
      return;
    }
    const answer = await post<{ site: { id: string }; location: { id: string }; warning: string | null }>(
      `${PAGES_PATH}/site-modal`,
      {
        companyId: forCompanyId,
        locationId: buildingId,
        location: buildingId ? null : { addressLines: addressLines ? [addressLines] : [], locality, administrativeArea, postalCode, countryCode: 'US' },
        suite: suite || null,
        name: name || null,
        siteTypeId: siteTypeId || null,
        note: note || null,
        // 🔒 Only a building this window creates takes the link's pin; a picked one keeps its own (decision 1600).
        pin: buildingId ? null : linkPin,
      },
      apiBaseUrl,
    );
    if (!answer.ok) {
      setError(answer.message);
      return;
    }
    onCreated({ siteId: answer.data.site.id, locationId: answer.data.location.id, warning: answer.data.warning, label: name || suite || picked?.label || addressLines || '' });
    onCancel();
  }, [addressLines, administrativeArea, apiBaseUrl, buildingId, forCompanyId, linkPin, locality, name, note, onCancel, onCreated, picked, postalCode, siteTypeId, suite]);

  /** A link read on step one fills the building's fields for the person to check, and its point waits for the save. */
  const fillFromLink = (answer: MapsLinkAnswer) => {
    setLinkPin(mapsLinkPin(answer));
    const address = answer.address;
    if (!address) return;
    if (address.addressLines[0]) setAddressLines(address.addressLines[0]);
    if (address.locality) setLocality(address.locality);
    if (address.administrativeArea) setAdministrativeArea(address.administrativeArea);
    if (address.postalCode) setPostalCode(address.postalCode);
  };

  return (
    <FormFrame
      host="window"
      open={open}
      title={step === 1 ? t('siteModal.stepOne') : t('siteModal.stepTwo')}
      subtitle={companyName ?? addressLine ?? t('siteModal.subject')}
      onCancel={onCancel}
      openerId={openerId}
      dataForm="site"
      typed={typed}
      act={step === 1 ? { label: t('employmentModal.next'), onAct: () => setStep(2) } : { label: t('modal.save'), onAct: () => save() }}
      refusal={shownError}
      onDismissRefusal={() => setError(null)}
    >
      <div data-site-step={step}>
        {shownWarning && <AlertBanner tone="warning" message={shownWarning} action={{ label: t('modal.dismiss'), onAction: () => setWarning(null) }} />}
        {step === 1 ? (
          <>
            <Dropdown size={Size.Medium}
              label={t('siteModal.existing')}
              entityLabel="Location"
              search={searchLocations}
              onSelect={(row) => setPicked({ id: row.id, label: row.line1 })}
              onAddNew={() => setPicked(null)}
              value={picked?.label ?? null}
            />
            {picked ? (
              <div className={styles.actions} data-site-picked="true">
                <span>{t('siteModal.picked')}</span>
                <Button size={Size.Small} text={[t('siteModal.typeInstead')]} actions={[() => setPicked(null)]} />
              </div>
            ) : (
              <>
                <p data-site-or-new="true">{t('siteModal.orNew')}</p>
                <MapsLinkPaste read={(link) => readMapsLinkAnswer(link, apiBaseUrl)} onPlace={fillFromLink} onCleared={() => setLinkPin(null)} />
                {/* 💰 An address created here is Unverified until a provider confirms it; the older promise of Verified-on-create is retired. */}
                <TextInput size={Size.Large} label={t('siteModal.address')} value={addressLines} onChange={setAddressLines} required />
                <TextInput size={Size.Medium} label={t('siteModal.locality')} value={locality} onChange={setLocality} />
                <TextInput size={Size.Medium} label={t('siteModal.administrativeArea')} value={administrativeArea} onChange={setAdministrativeArea} onBlur={() => setAdministrativeArea((was) => was.trim().toUpperCase())} />
                <TextInput label={t('siteModal.postalCode')} value={postalCode} onChange={setPostalCode} onBlur={() => setPostalCode((was) => normalizePostalCode(was))} validate={(typed) => { if (!typed.trim()) return null; const outcome = validatePostalCode(typed, 'US'); return outcome.ok ? null : outcome.message; }} chars={10} />
              </>
            )}
          </>
        ) : (
          <>
            {/* 🔨 The Footer holds a Primary, a Cancel and at most one destructive
                secondary (piece 5), so stepping back is a Body control. It is
                absent where the building is locked and there is no step one. */}
            {!locationId && <Button size={Size.Small} text={[t('employmentModal.back')]} actions={[() => setStep(1)]} />}
            {companyId === null && (
              <Dropdown size={Size.Medium}
                label={t('siteModal.company')}
                entityLabel="Company"
                search={searchCompanies}
                onSelect={(row) => setChosen({ id: row.id, label: row.line1 })}
                onAddNew={() => addNew.open({ kind: 'record', recordType: 'CompanyRecord' }, (row) => setChosen({ id: row.id, label: row.line1 }))}
                value={chosen?.label ?? null}
                required
              />
            )}
            {/* 🔨 The stored suite is never normalised: what the person typed is what the document prints. */}
            <TextInput size={Size.Medium} label={t('siteModal.suite')} value={suite} onChange={setSuite} onBlur={() => void checkCollision()} />
            <TextInput size={Size.Medium} label={t('siteModal.name')} value={name} onChange={setName} />
            {/* 🔒 Decision 10500: the Dropdown ending in Add New Site Type, for a holder of the list's write. */}
            <SiteTypeField label={t('siteModal.siteType')} value={siteTypeId} onChange={(chosen) => setSiteTypeId(chosen ?? '')} apiBaseUrl={apiBaseUrl} enabled={open} dataAttribute={{ 'data-site-type-select': 'true' }} />
            <TextInput size={Size.Large} label={t('siteModal.note')} value={note} onChange={setNote} />
          </>
        )}
      </div>
      {addNew.host}
    </FormFrame>
  );
}
